import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Branch, Company, Role, User } from '@core/models';
import {
  AuthService,
  BranchService,
  JwtService,
  UserService
} from '@core/services';
import { FeaturesEnabled } from '@core/services/application/menu-data.service';
import { Observable, map, of, switchMap, take, tap } from 'rxjs';
import * as Sentry from '@sentry/angular';

/**
 * Represents the logged in user's information.
 */
export interface AuthData {
  currentUser: User;
  currentUserRoles: Role[];
  currentCompany: Company;
  currentBranch: Branch;
  enabledFeatures: FeaturesEnabled[];
  isSuperAdmin: boolean;
  userType: string | null;
  hasInventory: boolean;
}

/**
 * Represents the logged in user's information.
 * Default values are set to null or empty arrays.
 *
 * @see {@link User}
 * @see {@link Company}
 * @see {@link Role}
 * @see {@link FeaturesEnabled}
 */
export class AuthDataImpl implements AuthData {
  currentUser: User;
  currentUserRoles: Role[];
  currentCompany: Company;
  currentBranch: Branch;
  enabledFeatures: FeaturesEnabled[];
  isSuperAdmin: boolean;
  userType: string | null;
  hasInventory: boolean;

  constructor(authData: Partial<AuthData>) {
    this.currentUser = authData.currentUser || null;
    this.currentUserRoles = authData.currentUserRoles || [];
    this.currentCompany = authData.currentCompany || null;
    this.currentBranch = authData.currentBranch || null;
    this.enabledFeatures = authData.enabledFeatures || [];
    this.isSuperAdmin = authData.isSuperAdmin || false;
    this.userType = authData.userType ?? null;
    this.hasInventory = authData.hasInventory || false;
  }
}

/**
 * Used to aggregate logged in Nymbl user information; company session and feature flags, user roles and privileges.
 * Supporting:
 * - AppMain/Layout component to dynamically render elements.
 * - Component access to user and company data.
 */
export const authDataResolver: ResolveFn<Observable<AuthData>> = () => {
  const _auth = inject(AuthService);
  const _user = inject(UserService);
  const _jwt = inject(JwtService);
  const _branch = inject(BranchService);
  let authData: AuthData = new AuthDataImpl({});

  // First fetch our current logged in user.
  return _auth.getCurrentUser().pipe(
    tap((user: User) => {
      authData.currentUser = user;
      authData.isSuperAdmin = user?.isSuperAdmin;
      Sentry.setUser({
        id: user.id,
        username: user.username,
        email: user.email
      });
    }),
    // Roles come from the auth service rather than a fetch of their own, so each
    // app supplies them the only way it can: nymbl3 from the `roles` claim it
    // already read on `/api/me` (the access token is HttpOnly, so `/api/me` is
    // the SPA's only route to a claim — ADR-002), nymbl2 from its legacy
    // per-user lookup. Only the claim is per-company, which is what makes a
    // multi-company user's roles follow their company selection in nymbl3.
    // `take(1)` because neither implementation's stream completes.
    switchMap(() => _auth.userRoles$.pipe(take(1))),
    // Set user roles defaulting to an empty array.
    tap((roles) => {
      authData.currentUserRoles = roles || [];
    }),
    // Fetch the user sessions company.
    switchMap(() => {
      return _auth
        .getCurrentCompany()
        ?.pipe(
          map((currentCompany) => (authData.currentCompany = currentCompany))
        );
    }),
    tap((currentCompany) =>
      Sentry.setTags({
        company_id: currentCompany.id,
        company_name: currentCompany.name
      })
    ),
    // Resolve the logged-in user's branch. `AuthService.getCurrentBranch()` is
    // only ever populated by an explicit branch switch (never on login), so the
    // branch is looked up directly by the JWT's `branchId` instead. This uses
    // `BranchService.get()`, a plain per-id HTTP call, rather than
    // `BranchService.getUserBranches$`, which is built once from `AuthService`'s
    // shared, never-completing BehaviorSubjects and isn't safe to re-enter from
    // within this same resolver's pipeline.
    switchMap(() => {
      const branchId = _jwt.getJwtData()?.branchId;
      if (!branchId) {
        authData.currentBranch = null;
        return of(null);
      }

      return _branch
        .get(branchId)
        .pipe(map((branch) => (authData.currentBranch = branch)));
    }),
    // Fetch feature flags
    switchMap(() => {
      return _auth
        .getFeatureFlags()
        ?.pipe(
          map(
            (featureFlags) =>
              (authData.enabledFeatures = featureFlags?.map((f) => [f, true]))
          )
        );
    }),
    // Fetch user privileges
    switchMap(() => {
      return _user.getAllPrivilegesByUserId(authData.currentUser?.id)?.pipe(
        tap((privileges) => {
          privileges.forEach((privilege) =>
            authData.enabledFeatures.push([privilege.name, true])
          );
        })
      );
    }),
    // Return the auth data
    map(() => {
      authData.hasInventory = authData.isSuperAdmin
        ? true
        : (authData.currentCompany?.inventory ?? false);
      return authData;
    })
  );
};
